import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserStoreService } from './user-store.service';
import { Environments } from '../models/environments.type';
import { SyncEntities } from '../models/sync-entities.type';

export interface ApiSynchronizerExportResponse {
  direction: "Export";
  batchId: string;
  organizationId: string;
  sourceEnvironment: string;
  destinationEnvironment: string;
  securityHashCode: string;
  generatedOn: string;
}

export interface ApiSynchronizerImportResponse {
  direction: "Import";
  batchId: string;
  organizationId: string;
  source: string;
  destination: string;
  hashSecurityCode: string;
  generatedOn: string;
}

export interface ApiSynchronizerHistoryResponse {
  items: SynchronizerHistoryItem[];
  totalCount: number;
  pageNumber: number;
  maxItems: number;
}

export interface SynchronizerHistoryItem {
  batchId: string;
  organizationId: string;
  sourceEnvironment: string;
  destinationEnvironment: string;
  securityHashCode: string;
  generatedOn: string;
  importStatus: "in progress" | "complete" | null;
  export: SynchronizerHistoryExportEntityDetails[];
  import: SynchronizerHistoryImportEntityDetails[];
}

export interface SynchronizerHistoryEntityDetails {
  entity: SyncEntities;
  startedAt: string;
  completedAt: string;
  totalCount: number;
  failureCount: number;
  skippedCount: number;
}

export interface SynchronizerHistoryExportEntityDetails extends SynchronizerHistoryEntityDetails {
}

export interface SynchronizerHistoryImportEntityDetails extends SynchronizerHistoryEntityDetails {
  successCount: number;
}

@Injectable({ providedIn: 'root' })
export class ApiSynchronizerService {
  private readonly apiBaseUrl = environment.apiBaseUrl;

  constructor(
    private http: HttpClient,
    private userStore: UserStoreService) {}

  syncExport(organizationId: string, source: Environments, destination: Environments, entities: SyncEntities[], exclude: SyncEntities[], global: boolean): Observable<ApiSynchronizerExportResponse> {
    //Prevent update of other surveys during development, unless the user is a SuperAdmin
    if(organizationId != 'a535f93d-f8c7-421d-a9c9-74f7a17e3761' && !this.userStore.isSuperAdmin()){
      return throwError(() => new Error('This organization/survey is not editable.'));
    }

    const request = {
      organizationId: organizationId,
      sourceEnvironment: source,
      destinationEnvironment: destination,
      entities: entities,
      entitiesToExclude: exclude,
      includeGlobalConfigurations: global
    }

    return this.http.post<ApiSynchronizerExportResponse>(`${this.apiBaseUrl}/synchronizerexport`, request);
  }

  syncImport(organizationId: string, source: string, destination: Environments, securityHashCode: string, batchId: string): Observable<ApiSynchronizerImportResponse> {
    //Prevent update of other surveys during development, unless the user is a SuperAdmin
    if(organizationId != 'a535f93d-f8c7-421d-a9c9-74f7a17e3761' && !this.userStore.isSuperAdmin()){
      return throwError(() => new Error('This organization/survey is not editable.'));
    }

    const request = {
      organizationId: organizationId,
      sourceEnvironment: source,
      destinationEnvironment: destination,
      securityHashCode: securityHashCode,
      batchId: batchId
    }

    return this.http.post<ApiSynchronizerImportResponse>(`${this.apiBaseUrl}/synchronizerimport`, request);
  }

  syncHistory(organizationId: string, pageNumber: number, maxItems: number): Observable<ApiSynchronizerHistoryResponse> {
    const params = new HttpParams()
      .set('organizationId', organizationId)
      .set('pageNumber', pageNumber)
      .set('maxItems', maxItems);

    return this.http.get<ApiSynchronizerHistoryResponse>(`${this.apiBaseUrl}/synchronizerhistory`, {params});
  }
}